/**
 * Vocabulary: readers see this feature as "owner reviews"; the code, routes, DealHunt paths
 * (`/api/owner/*`) and analytics events keep the historical "owner note" name on purpose — do not
 * rename them.
 *
 * The reader's own note on one product (backlog R1): the `/me` fetch, the own-note lookup, the
 * pending state and the patches after publish / claim / delete / forget — written once, for the
 * PDP Owners section and the category tile. Client state only: nothing here runs in a server
 * render (the product HTML is edge-cached, #51), and the device marker is read in effects only.
 *
 * Surface-local numbers stay with the surface: the section's owner count and wall, the tile's
 * `publishedHere`, and the rail signal (`publishOwnNote`, PDP only). The apply functions return
 * what those callers branch on.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  OWNER_HELD_REVIEW_MESSAGE,
  type OwnerClaimResponse,
  type OwnerMeResponse,
  type OwnerNoteOwn,
  type OwnerNoteViews,
  type OwnerPublishResponse,
} from 'lib/dealhunt/types'
import { forgetSharedMe, sharedMe } from './api'
import { claimPublishedThisPage, shelfItemMatches } from './band-copy'
import { hasDeviceMarker, notifyDeviceChanged, onDeviceChanged, useDeviceEpoch } from './declared'
import { NEW_REVIEW_PATH } from 'lib/community/model'
import { consumePublishedMarks, isPublishedMarkKey } from 'lib/community/writer-handoff'

/** Ask `/me` early — the reader opened a product search — so the answer is in hand by the time
 *  a pick resolves, instead of starting after it. Only with a device (no device = no profile). */
export function prefetchMe(): void {
  if (hasDeviceMarker()) {
    void sharedMe()
  }
}

// A device change (sign-in relink, a lapsed session) can happen without a write through `call`.
onDeviceChanged(forgetSharedMe)

// ── back from the community writer (One Review Writer, P1) ──────────────────────
// The writer sets `sg:review-published:<slug>` when DealHunt answers a publish. A page the reader
// returns to must not show the ask again from a `/me` it read before: on mount (a client-side
// return, a reload) the mark makes the first `/me` a fresh one; on a back-forward-cache restore
// (`pageshow` persisted — the page never remounts) it re-asks every mounted `useOwnNote`; from a
// writer opened in a NEW tab (`?tab=new`) the mark is in localStorage and arrives by the `storage`
// event (or the next `visibilitychange` to visible). Its own
// signal, not `notifyDeviceChanged`: that one also wakes the session's relink logic.

const refreshListeners = new Set<() => void>()

function localStore(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** True — and the marks are gone — when a review was published in this tab since the last read.
 *  The writer itself never consumes them: they are for the page the reader goes back to. */
export function takePublishedMark(): boolean {
  if (window.location.pathname.startsWith(NEW_REVIEW_PATH)) {
    return false
  }
  // Both stores, always: a same-tab return leaves it in sessionStorage, a new-tab writer in
  // localStorage (the other tab).
  const here = consumePublishedMarks(sessionStore())
  const elsewhere = consumePublishedMarks(localStore())
  return here || elsewhere
}

function rereadOwnNotes(): void {
  forgetSharedMe()
  refreshListeners.forEach(fn => fn())
}

/**
 * Every `useOwnNote` on the page asks `/me` again — after a write one surface patched locally
 * (the proofread Undo) so the others catch up. Never `notifyDeviceChanged`: session.ts relinks
 * on it.
 */
export function refreshOwnNotes(): void {
  rereadOwnNotes()
}

/** Hear the back-forward-cache re-read (`useOwnNote` subscribes); returns the unsubscribe. */
export function onOwnNoteRefresh(fn: () => void): () => void {
  refreshListeners.add(fn)
  return () => {
    refreshListeners.delete(fn)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', event => {
    if (event.persisted && takePublishedMark()) {
      rereadOwnNotes()
    }
  })
  // The new-tab handoff: the writer (another tab) set the mark in localStorage. The `storage`
  // event reaches this tab at once; `visibilitychange` covers a missed event (the tab was
  // discarded and restored, storage blocked for events) when the reader comes back to it.
  window.addEventListener('storage', event => {
    if (isPublishedMarkKey(event.key) && event.newValue && takePublishedMark()) {
      rereadOwnNotes()
    }
  })
  window.document?.addEventListener('visibilitychange', () => {
    if (window.document.visibilityState === 'visible' && takePublishedMark()) {
      rereadOwnNotes()
    }
  })
}

/** The reader's note on this product from `/me` — matched through its alias group (#110). */
export function ownNoteFromMe(me: OwnerMeResponse | null, nodeId: string): OwnerNoteOwn | null {
  return me?.shelf.find(item => shelfItemMatches(item, nodeId))?.note ?? null
}

/** The shelf row's views for this product (#140); null without one. */
export function ownViewsFromMe(
  me: OwnerMeResponse | null,
  nodeId: string | null
): OwnerNoteViews | null {
  if (!nodeId) {
    return null
  }
  return me?.shelf.find(item => shelfItemMatches(item, nodeId))?.views ?? null
}

/** `/me` after a sign-in linked this browser's profile: claimed, the account's name and email. */
export function meAfterClaim(
  me: OwnerMeResponse | null,
  claim: OwnerClaimResponse
): OwnerMeResponse | null {
  if (!me) {
    return me
  }
  return {
    ...me,
    profile: {
      ...me.profile,
      claimed: true,
      handle: claim.handle,
      email_masked: claim.email_masked,
    },
  }
}

/**
 * Did this claim publish the note on `nodeId`? `claimPublishedThisPage` (#120) with the shelf row
 * from the `/me` read BEFORE the claim — the row that carries the note's aliases.
 */
export function claimLiveHere(
  claim: OwnerClaimResponse,
  note: OwnerNoteOwn | null,
  nodeId: string | null,
  me: OwnerMeResponse | null
): boolean | null {
  if (!nodeId) {
    return false
  }
  return claimPublishedThisPage(
    claim,
    note,
    nodeId,
    me?.shelf.find(item => shelfItemMatches(item, nodeId)) ?? null
  )
}

/** The own note after a sign-in: the account's name (a merge renames it) and, when DealHunt
 *  named this product's held note (`liveHere`), published now. */
export function ownNoteAfterClaim(
  note: OwnerNoteOwn | null,
  claim: OwnerClaimResponse,
  liveHere: boolean | null
): OwnerNoteOwn | null {
  if (!note) {
    return note
  }
  const renamed = { ...note, handle: claim.handle }
  return liveHere ? { ...renamed, status: 'published', reader_message: null } : renamed
}

/**
 * After a claim, must `/me` be asked again instead of trusting the local patch? When DealHunt
 * predates #120 (`liveHere === null`), and when this product's note is LIVE with an accepted
 * anonymous edit waiting (#129, the review message): the claim published that edit, but
 * `claimPublishedThisPage` matches held notes only and the local card still holds the old
 * pending state. Re-read — never patch a status or a count from it (#120).
 */
export function claimNeedsReread(liveHere: boolean | null, note: OwnerNoteOwn | null): boolean {
  if (liveHere === null) {
    return true
  }
  return note?.status === 'published' && note.reader_message === OWNER_HELD_REVIEW_MESSAGE
}

export interface OwnNoteState {
  /** This browser's `/me`; null while unknown, without a device, or after a forget. */
  me: OwnerMeResponse | null
  /** The reader's note (any status) on this product. */
  ownNote: OwnerNoteOwn | null
  claimed: boolean
  /** A device marker exists and `/me` has not answered yet. */
  pending: boolean
  /** The band's publish answered: the author's card comes straight from it (#51/§6). */
  applyResult: (result: OwnerPublishResponse) => void
  /** A sign-in linked the profile. Returns whether it published THIS product's note (#120):
   *  `null` = a DealHunt without `published_node_ids`, and `/me` is asked again. */
  applyClaim: (claim: OwnerClaimResponse) => boolean | null
  /** The own note was deleted. Returns the note that was there. */
  applyDeleted: () => OwnerNoteOwn | null
  /** The band's "start fresh on this browser": the device is gone — every owner surface re-checks. */
  clearDevice: () => void
}

export function useOwnNote(nodeId: string | null): OwnNoteState {
  const [me, setMe] = useState<OwnerMeResponse | null>(null)
  const [ownNote, setOwnNote] = useState<OwnerNoteOwn | null>(null)
  const [claimed, setClaimed] = useState(false)
  const [pending, setPending] = useState(false)
  // The apply functions run from async band code holding an older render's props: read the
  // current state, not the closure's.
  const latest = useRef({ me, ownNote, nodeId })
  latest.current = { me, ownNote, nodeId }
  // Counts local patches. A `/me` asked before one answers with the state before it — drop that
  // answer and ask again rather than wipe a note that was just published.
  const patches = useRef(0)
  const [reask, setReask] = useState(0)

  const deviceEpoch = useDeviceEpoch()
  useEffect(() => onOwnNoteRefresh(() => setReask(n => n + 1)), [])
  useEffect(() => {
    if (takePublishedMark()) {
      forgetSharedMe()
    }
    if (!nodeId || !hasDeviceMarker()) {
      // Logout forgot this browser (no reload under commentHub): nothing of the author stays.
      setMe(null)
      setOwnNote(null)
      setClaimed(false)
      setPending(false)
      return undefined
    }
    let cancelled = false
    const asked = patches.current
    // Set in the first effect flush, so the first hydrated render already holds the ask back
    // instead of showing it until `/me` answers.
    setPending(true)
    sharedMe().then(
      res => {
        if (cancelled) {
          return
        }
        if (patches.current !== asked) {
          setReask(n => n + 1)
          return
        }
        setClaimed(res.profile.claimed)
        setMe(res)
        setOwnNote(ownNoteFromMe(res, nodeId))
        setPending(false)
      },
      () => {
        // No profile on this device or the service is down — the surface stays public-only.
        if (!cancelled) {
          setPending(false)
        }
      }
    )
    return () => {
      cancelled = true
    }
  }, [nodeId, deviceEpoch, reask])

  const applyResult = useCallback((result: OwnerPublishResponse): void => {
    patches.current += 1
    setOwnNote(result.note)
  }, [])

  const applyClaim = useCallback((claim: OwnerClaimResponse): boolean | null => {
    // Before any patch: the match reads the pre-claim note and shelf row.
    const now = latest.current
    const liveHere = claimLiveHere(claim, now.ownNote, now.nodeId, now.me)
    patches.current += 1
    setClaimed(true)
    setMe(m => meAfterClaim(m, claim))
    setOwnNote(n => ownNoteAfterClaim(n, claim, liveHere))
    if (claimNeedsReread(liveHere, now.ownNote)) {
      // A DealHunt without #120, or a live note's accepted edit (#129): `/me` says what the
      // claim really published.
      notifyDeviceChanged()
    }
    return liveHere
  }, [])

  const applyDeleted = useCallback((): OwnerNoteOwn | null => {
    const gone = latest.current.ownNote
    patches.current += 1
    setOwnNote(null)
    return gone
  }, [])

  const clearDevice = useCallback((): void => {
    patches.current += 1
    setMe(null)
    setClaimed(false)
    setOwnNote(null)
    notifyDeviceChanged()
  }, [])

  return { me, ownNote, claimed, pending, applyResult, applyClaim, applyDeleted, clearDevice }
}
